import { useEffect, useRef, useState } from 'react';
import usePrevious from '../../../hooks/usePrevious';
import { useEmbeddedCommentClickListener } from '../hooks/useEmbeddedCommentClickListener';
import { useToggleOpenPopover } from '../hooks/useToggleOpenPopover';
import { useEmbeddedCommentHandlers } from '../hooks/useEmbeddedCommentHandlers';
import useWindowScrollListener from '../../../hooks/useWindowScrollListener';
import ChildrenWrapper from 'UIComponents/utils/ChildrenWrapper';
import { findDOMNode } from 'react-dom';
import { jsx as _jsx } from "react/jsx-runtime";
export default function ControlledEmbeddedCommentPopover({
  moduleId,
  description,
  collaborationSidebarState,
  onUpdateCollaborationSidebarState,
  isOpen,
  onOpenChange,
  children,
  popoverPlacement = 'top',
  nodeRef
}) {
  const {
    isEmbeddedPopoverOpen
  } = collaborationSidebarState;
  const prevIsOpen = usePrevious(isOpen);
  const prevIsEmbeddedPopoverOpen = usePrevious(isEmbeddedPopoverOpen);
  const embeddedCommentAnchor = useRef(null);
  const [childElement, setChildElement] = useState(null);
  useEffect(() => {
    if (nodeRef !== null && nodeRef !== void 0 && nodeRef.current) {
      setChildElement(nodeRef.current);
      return;
    }
    /*
    The ContentEditorUI embeds the ControlledEmbeddedCommentPopover into it's own toolbar,
    which we can't wrap without breaking the behavior.
    We use the ChildrenWrapper and the findDOMNode to get the coordinates
    and the dimensions of the wrapped content.
    The UIPopover component does the same behind the scenes.
    */
    /* eslint-disable-next-line react/no-find-dom-node */
    const element = findDOMNode(embeddedCommentAnchor.current);
    if (element instanceof Element) {
      setChildElement(element);
    }
  }, [embeddedCommentAnchor, children, childElement, nodeRef]);
  const toggleOpenPopover = useToggleOpenPopover({
    embeddedCommentWrapper: childElement,
    onUpdateCollaborationSidebarState,
    moduleId,
    metaDescription: description,
    placement: popoverPlacement
  });
  useEffect(() => {
    // The open state is externally controlled, so we need to call the toggleOpenPopover
    if (isOpen !== prevIsOpen) {
      toggleOpenPopover(isOpen);
    }
  }, [isOpen, prevIsOpen, toggleOpenPopover]);
  useEffect(() => {
    // The popover can be only open because of external events,
    // but it can be closed by both external events and internal events
    // so if the popover is closed by internal library events, we need to notify the parent component
    if (!isEmbeddedPopoverOpen && prevIsEmbeddedPopoverOpen && onOpenChange) {
      onOpenChange(isEmbeddedPopoverOpen);
    }
  }, [onOpenChange, isEmbeddedPopoverOpen, prevIsEmbeddedPopoverOpen]);
  const {
    updateEmbeddedCommentCoordinates
  } = useEmbeddedCommentHandlers({
    popoverPlacement,
    embeddedCommentWrapper: childElement,
    onUpdateCollaborationSidebarState
  });
  useWindowScrollListener({
    listenToScroll: isOpen,
    onScroll: updateEmbeddedCommentCoordinates,
    useCapture: true
  });
  useEmbeddedCommentClickListener({
    onUpdateCollaborationSidebarState,
    isOpen,
    prevIsOpen
  });
  return /*#__PURE__*/_jsx(ChildrenWrapper, {
    ref: embeddedCommentAnchor,
    children: children
  });
}